import React, { useRef } from "react";
import { LMPSurfaceResult } from "../../powerFlow/lmpSurface";
import { useElementDimensions } from "../../hooks/useElementDimensions";

interface Props {
  busId: string | null;
  numTimeSteps: number;
  result: LMPSurfaceResult | null;
  loading: boolean;
  onClear: () => void;
  hoverHour?: number | null;
  onHourHover?: (hour: number | null) => void;
}

const containerStyle: React.CSSProperties = {
  padding: "8px",
  background: "#f9f9f9",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const overlayStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#999",
  fontSize: "12px",
  textAlign: "center",
  padding: "0 20px",
};

/** Price → color: green ($0) through yellow to red ($priceMax). */
function priceColor(price: number, priceMax: number): string {
  const f = Math.max(0, Math.min(1, price / priceMax));
  const hue = 120 * (1 - f); // 120 = green, 0 = red
  return `hsl(${hue}, 70%, 45%)`;
}

const BusLMPSurfaceChart: React.FC<Props> = ({ busId, numTimeSteps, result, loading, onClear, hoverHour, onHourHover }) => {
  // The measured container is always mounted (below), so it is sized correctly
  // even while the "Computing…" / "Select a bus" overlays are showing.
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useElementDimensions(containerRef, undefined, undefined, {
    defaultWidth: 700,
    defaultHeight: 220,
    widthOffset: 8,
    heightOffset: 0,
  });

  const header = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <h4 style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#333" }}>
        {busId ? `LMP Surface — ${busId}` : "LMP Surface"}
      </h4>
      {busId && (
        <button
          onClick={onClear}
          title="Clear selection"
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", color: "#888", lineHeight: 1 }}
        >
          ✕
        </button>
      )}
    </div>
  );

  const hoursDone = result?.hours.length ?? 0;
  const overlayMsg = !busId
    ? "Select a bus (radio in a node table, or click a node) to see its LMP across the day, colored by price."
    : loading && hoursDone === 0
    ? "Computing LMP surface…"
    : !result || result.hours.length === 0 || result.maxDemandMW <= 0
    ? "No feasible dispatch for this bus."
    : null;

  // --- chart geometry (only used when there is a result to draw) ---
  const width = dimensions.width;
  const height = dimensions.height;
  const leftMargin = 52;
  const rightMargin = 64; // room for the color legend
  const topMargin = 12;
  const bottomMargin = 34;
  const plotW = Math.max(1, width - leftMargin - rightMargin);
  const plotH = Math.max(1, height - topMargin - bottomMargin);
  const slot = plotW / numTimeSteps;
  const xLeft = (t: number) => leftMargin + t * slot + Math.min(3, slot * 0.15) / 2;
  const barW = Math.max(1, slot - Math.min(3, slot * 0.15));
  const xCenter = (t: number) => xLeft(t) + barW / 2;

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!onHourHover) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xUser = (e.clientX - rect.left) * (width / rect.width);
    if (xUser < leftMargin || xUser > leftMargin + plotW) return;
    const hour = Math.floor((xUser - leftMargin) / slot);
    onHourHover(Math.max(0, Math.min(numTimeSteps - 1, hour)));
  };

  let chart: React.ReactNode = null;
  if (!overlayMsg && result) {
    const yMax = result.maxDemandMW;
    const yToPx = (mw: number) => topMargin + plotH - (mw / yMax) * plotH;
    const yTicks = niceTicks(0, yMax, 5);
    const xTickEvery = numTimeSteps > 12 ? 6 : numTimeSteps > 6 ? 3 : 1;

    const loadPts = result.isLoad
      ? result.hours
          .filter((h) => h.loadMW != null)
          .map((h) => `${xCenter(h.hour)},${yToPx(h.loadMW as number)}`)
          .join(" ")
      : "";

    const legendX = width - rightMargin + 20;
    const legendW = 12;
    const legendTop = topMargin + 6;
    const legendH = Math.max(20, plotH - 12);

    const showHover = hoverHour != null && hoverHour >= 0 && hoverHour < numTimeSteps;

    chart = (
      <svg
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => onHourHover?.(null)}
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "1px solid #ddd", background: "white" }}
      >
        <defs>
          <linearGradient id="lmp-price-gradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={priceColor(0, result.priceMax)} />
            <stop offset="50%" stopColor={priceColor(result.priceMax / 2, result.priceMax)} />
            <stop offset="100%" stopColor={priceColor(result.priceMax, result.priceMax)} />
          </linearGradient>
        </defs>

        {/* y grid + ticks */}
        {yTicks.map((t) => (
          <g key={`y${t}`}>
            <line x1={leftMargin} y1={yToPx(t)} x2={leftMargin + plotW} y2={yToPx(t)} stroke="#eee" strokeWidth={1} />
            <text x={leftMargin - 6} y={yToPx(t) + 3} textAnchor="end" fontSize="9" fill="#666">
              {t.toFixed(0)}
            </text>
          </g>
        ))}

        {/* stacked, price-colored bars per timestep */}
        {result.hours.map((h) =>
          h.segments.map((seg, i) => {
            const yTop = yToPx(seg.toMW);
            const yBot = yToPx(seg.fromMW);
            return (
              <rect key={`${h.hour}-${i}`} x={xLeft(h.hour)} y={yTop} width={barW} height={Math.max(0.5, yBot - yTop)} fill={priceColor(seg.lmp, result.priceMax)}>
                <title>{`hour ${h.hour}: ${seg.fromMW.toFixed(0)}–${seg.toMW.toFixed(0)} MW @ $${seg.lmp.toFixed(2)}/MWh`}</title>
              </rect>
            );
          })
        )}

        {/* load line (with a white halo so it reads over the colored bars) */}
        {result.isLoad && loadPts && (
          <>
            <polyline points={loadPts} fill="none" stroke="#fff" strokeWidth={4} strokeLinejoin="round" opacity={0.9} />
            <polyline points={loadPts} fill="none" stroke="#111" strokeWidth={2} strokeLinejoin="round" />
            {result.hours
              .filter((h) => h.loadMW != null)
              .map((h) => (
                <circle key={`ld${h.hour}`} cx={xCenter(h.hour)} cy={yToPx(h.loadMW as number)} r={2} fill="#111" stroke="#fff" strokeWidth={0.75} />
              ))}
          </>
        )}

        {/* current-timestep marker (linked to the other plots) */}
        {showHover && (
          <line x1={xCenter(hoverHour as number)} y1={topMargin} x2={xCenter(hoverHour as number)} y2={topMargin + plotH} stroke="red" strokeWidth={2} strokeDasharray="4,4" pointerEvents="none" />
        )}

        {/* axes */}
        <line x1={leftMargin} y1={topMargin} x2={leftMargin} y2={topMargin + plotH} stroke="#999" strokeWidth={1} />
        <line x1={leftMargin} y1={topMargin + plotH} x2={leftMargin + plotW} y2={topMargin + plotH} stroke="#999" strokeWidth={1} />

        {/* x ticks (hours) */}
        {Array.from({ length: numTimeSteps }, (_, t) => t)
          .filter((t) => t % xTickEvery === 0)
          .map((t) => (
            <text key={`x${t}`} x={xCenter(t)} y={topMargin + plotH + 13} textAnchor="middle" fontSize="9" fill="#666">
              {t}
            </text>
          ))}

        {/* axis labels */}
        <text x={leftMargin + plotW / 2} y={height - 3} textAnchor="middle" fontSize="10" fill="#333">
          Hour
        </text>
        <text x={11} y={topMargin + plotH / 2} textAnchor="middle" fontSize="10" fill="#333" transform={`rotate(-90 11 ${topMargin + plotH / 2})`}>
          Demand (MW)
        </text>

        {/* price color legend */}
        <rect x={legendX} y={legendTop} width={legendW} height={legendH} fill="url(#lmp-price-gradient)" stroke="#ccc" strokeWidth={0.5} />
        <text x={legendX + legendW + 3} y={legendTop + 8} fontSize="9" fill="#666">
          ${result.priceMax.toFixed(0)}
        </text>
        <text x={legendX + legendW + 3} y={legendTop + legendH} fontSize="9" fill="#666">
          $0
        </text>
        <text x={legendX + legendW / 2} y={legendTop - 3} textAnchor="middle" fontSize="8" fill="#888">
          LMP
        </text>
      </svg>
    );
  }

  return (
    <div style={containerStyle}>
      {header}
      <div ref={containerRef} style={{ flex: 1, position: "relative", minHeight: 0 }}>
        {overlayMsg ? <div style={overlayStyle}>{overlayMsg}</div> : chart}
      </div>
    </div>
  );
};

/** Produce ~count "nice" round tick values spanning [min, max]. */
function niceTicks(min: number, max: number, count: number): number[] {
  if (max <= min) return [min];
  const span = max - min;
  const rawStep = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const niceStep = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const ticks: number[] = [];
  for (let t = Math.ceil(min / niceStep) * niceStep; t <= max + 1e-9; t += niceStep) {
    ticks.push(Math.round(t * 1000) / 1000);
  }
  return ticks;
}

export default BusLMPSurfaceChart;
